/**
 * @engine/vfx — render-shader codegen (vertex + fragment WGSL).
 *
 * Split out of ShaderGenerator.ts: self-contained string builders that depend only on
 * RenderOptions + the vertex-input attribute locations. No compute-side coupling.
 */

import { VERTEX_INPUT_LOCATIONS } from './Layouts';

/**
 * Render options extracted from the output node (drives vertex/fragment codegen).
 */
export interface RenderOptions {
  /** Render type: 'quad', 'sprite', 'point', 'line' */
  renderType: 'quad' | 'sprite' | 'point' | 'line' | 'trail' | 'mesh';
  /** Whether this particle system receives Forward+ lighting */
  isLit: boolean;
  /** Whether to render soft circular particles (vs hard-edged quads) */
  softParticle: boolean;
  /** Stretch mode: 0=none, 1=velocity, 2=custom */
  stretchMode: number;
  /** Stretch scale multiplier */
  stretchScale: number;
  /** Blend mode: 0=alpha, 1=additive, 2=multiply, 3=premultiplied */
  blendMode: number;
  /** Orient mode: 0=FaceCameraPosition (uses rotation), 1=FaceCameraPlane (ignores rotation) */
  orientMode: number;
  /** For sprite: flipbook columns */
  flipbookColumns: number;
  /** For sprite: flipbook rows */
  flipbookRows: number;
  /** For line: length multiplier */
  lineLength: number;
  /** For line: width */
  lineWidth: number;
  /** For line: align to velocity */
  velocityAlign: boolean;
}

/**
 * Generate the vertex shader for particle rendering.
 * Exported for use by legacy codegen.ts
 * Uses VERTEX_INPUT_LOCATIONS from StructLayout.ts for attribute locations.
 */
export function generateVertexShader(options?: RenderOptions): string {
  const opts = options ?? { 
    renderType: 'quad', isLit: false, softParticle: true, stretchMode: 0, stretchScale: 1.0,
    blendMode: 0, orientMode: 0, flipbookColumns: 4, flipbookRows: 4,
    lineLength: 1.0, lineWidth: 0.5, velocityAlign: true
  };
  const loc = VERTEX_INPUT_LOCATIONS;
  
  // Generate velocity-aligned stretching code if needed
  const stretchCode = opts.stretchMode === 1 ? `
  // Velocity-aligned stretching
  let velLen = length(input.velocity);
  if (velLen > 0.001) {
    let velDir = input.velocity / velLen;
    // Stretch along velocity direction
    let stretchFactor = 1.0 + velLen * ${opts.stretchScale.toFixed(4)};
    // Compute tangent (perpendicular to velocity)
    let tangent = vec2<f32>(-velDir.y, velDir.x);
    // Transform quad: stretch along velocity, keep width along tangent
    let stretchedPos = vec2<f32>(
      quadPos.x * stretchFactor * velDir.x + quadPos.y * tangent.x,
      quadPos.x * stretchFactor * velDir.y + quadPos.y * tangent.y
    );
    rotatedPos = stretchedPos;
  }` : '';

  // Generate line-specific vertex shader
  if (opts.renderType === 'line') {
    return `
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
  @location(1) color: vec4<f32>,
}

struct VertexInput {
  @builtin(vertex_index) vertexIndex: u32,
  @builtin(instance_index) instanceIdx: u32,
  @location(${loc.particlePos}) particlePos: vec3<f32>,
  @location(${loc.velocity}) velocity: vec3<f32>,
  @location(${loc.color}) color: vec4<f32>,
  @location(${loc.size}) size: f32,
  @location(${loc.scale}) scale: vec2<f32>,
  @location(${loc.rotation}) rotation: f32,
  @location(${loc.lifetime}) lifetime: f32,
  @location(${loc.maxLifetime}) maxLifetime: f32,
  @location(${loc.alive}) alive: u32,
}

@vertex
fn vs_main(input: VertexInput) -> VertexOutput {
  // Line vertices: 6 vertices for stretched quad along velocity
  let positions = array<vec2<f32>, 6>(
    vec2<f32>(-0.5, -0.5),
    vec2<f32>( 0.5, -0.5),
    vec2<f32>(-0.5,  0.5),
    vec2<f32>( 0.5, -0.5),
    vec2<f32>( 0.5,  0.5),
    vec2<f32>(-0.5,  0.5),
  );
  let uvs = array<vec2<f32>, 6>(
    vec2<f32>(0.0, 1.0),
    vec2<f32>(1.0, 1.0),
    vec2<f32>(0.0, 0.0),
    vec2<f32>(1.0, 1.0),
    vec2<f32>(1.0, 0.0),
    vec2<f32>(0.0, 0.0),
  );

  var output: VertexOutput;
  
  if (input.alive == 0u) {
    output.position = vec4<f32>(0.0, 0.0, -10.0, 1.0);
    output.uv = vec2<f32>(0.0, 0.0);
    output.color = vec4<f32>(0.0, 0.0, 0.0, 0.0);
    return output;
  }
  
  let quadPos = positions[input.vertexIndex % 6u];
  
  // Emitter scale
  let rawEmitterScale = vec2<f32>(vfxUniforms.emitterScaleX, vfxUniforms.emitterScaleY);
  let emitterScale = select(rawEmitterScale, vec2<f32>(1.0, 1.0), rawEmitterScale.x == 0.0 && rawEmitterScale.y == 0.0);
  let avgScale = (emitterScale.x + emitterScale.y) * 0.5;
  
  // Velocity-aligned stretching for lines
  let velLen = length(input.velocity);
  var stretchedPos: vec2<f32>;
  
  if (velLen > 0.001) {
    let velDir = normalize(input.velocity.xy);
    let tangent = vec2<f32>(-velDir.y, velDir.x);
    // X is along velocity (length), Y is perpendicular (width)
    let lineLen = ${opts.lineLength.toFixed(4)} * input.size * avgScale;
    let lineW = ${opts.lineWidth.toFixed(4)} * input.size * avgScale;
    stretchedPos = velDir * quadPos.x * lineLen + tangent * quadPos.y * lineW;
  } else {
    // No velocity - render as small circle
    stretchedPos = quadPos * input.size * ${opts.lineWidth.toFixed(4)} * avgScale;
  }
  
  // Particle positions are already in world space (scaled + offset at spawn time)
  let worldPos = input.particlePos + vec3<f32>(stretchedPos, 0.0);
  
  output.position = vfxUniforms.viewProjection * vec4<f32>(worldPos, 1.0);
  output.uv = uvs[input.vertexIndex % 6u];
  output.color = input.color;
  return output;
}`;
  }
  
  // Standard quad/sprite vertex shader
  const litWorldPosField = opts.isLit ? `\n  @location(2) worldPos: vec2<f32>,` : '';
  return `
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
  @location(1) color: vec4<f32>,${litWorldPosField}
}

struct VertexInput {
  @builtin(vertex_index) vertexIndex: u32,
  @builtin(instance_index) instanceIdx: u32,
  @location(${loc.particlePos}) particlePos: vec3<f32>,
  @location(${loc.velocity}) velocity: vec3<f32>,
  @location(${loc.color}) color: vec4<f32>,
  @location(${loc.size}) size: f32,
  @location(${loc.scale}) scale: vec2<f32>,
  @location(${loc.rotation}) rotation: f32,
  @location(${loc.lifetime}) lifetime: f32,
  @location(${loc.maxLifetime}) maxLifetime: f32,
  @location(${loc.alive}) alive: u32,
}

@vertex
fn vs_main(input: VertexInput) -> VertexOutput {
  // Quad vertices (2 triangles)
  let positions = array<vec2<f32>, 6>(
    vec2<f32>(-0.5, -0.5),
    vec2<f32>( 0.5, -0.5),
    vec2<f32>(-0.5,  0.5),
    vec2<f32>( 0.5, -0.5),
    vec2<f32>( 0.5,  0.5),
    vec2<f32>(-0.5,  0.5),
  );
  let uvs = array<vec2<f32>, 6>(
    vec2<f32>(0.0, 1.0),
    vec2<f32>(1.0, 1.0),
    vec2<f32>(0.0, 0.0),
    vec2<f32>(1.0, 1.0),
    vec2<f32>(1.0, 0.0),
    vec2<f32>(0.0, 0.0),
  );

  var output: VertexOutput;
  
  // Use a fixed small size for debug visualization of dead particles
  var effectiveSize = input.size;
  // Use scale if set, otherwise default to (1, 1)
  var effectiveScale = select(vec2<f32>(1.0, 1.0), input.scale, input.scale.x > 0.0 || input.scale.y > 0.0);
  var outputColor = input.color;
  
  // Dead particles: move off-screen with zero alpha
  if (input.alive == 0u) {
    output.position = vec4<f32>(0.0, 0.0, -10.0, 1.0);
    output.uv = vec2<f32>(0.0, 0.0);
    output.color = vec4<f32>(0.0, 0.0, 0.0, 0.0);${opts.isLit ? `\n    output.worldPos = vec2<f32>(0.0, 0.0);` : ''}
    return output;
  }
  
  // The particle is alive - render normally
  let rawEmitterScale = vec2<f32>(vfxUniforms.emitterScaleX, vfxUniforms.emitterScaleY);
  let emitterScale = select(rawEmitterScale, vec2<f32>(1.0, 1.0), rawEmitterScale.x == 0.0 && rawEmitterScale.y == 0.0);
  let avgScale = (emitterScale.x + emitterScale.y) * 0.5;
  let quadPos = positions[input.vertexIndex % 6u] * effectiveSize * effectiveScale * avgScale;

  // Apply rotation (only for Face Camera Position mode, skipped for Face Camera Plane)
  ${opts.orientMode === 1 ? `
  // Face Camera Plane: ignore particle rotation, always face camera
  var rotatedPos = quadPos;` : `
  // Face Camera Position: apply particle rotation
  let c = cos(input.rotation);
  let s = sin(input.rotation);
  var rotatedPos = vec2<f32>(
    quadPos.x * c - quadPos.y * s,
    quadPos.x * s + quadPos.y * c
  );`}
${stretchCode}

  // Particle positions are already in world space (scaled + offset at spawn time)
  let worldPos = input.particlePos + vec3<f32>(rotatedPos, 0.0);

  output.position = vfxUniforms.viewProjection * vec4<f32>(worldPos, 1.0);
  output.uv = uvs[input.vertexIndex % 6u];
  output.color = outputColor;${opts.isLit ? `\n  output.worldPos = worldPos.xy;` : ''}
  return output;
}`;
}

/**
 * Generate the fragment shader.
 * NOTE: VertexOutput struct defined in vertex shader, they'll be concatenated.
 * @param usesTexture - Whether the output node uses textures (quad/sprite) or not (point/line)
 * @param options - Render options from output node
 */
export function generateFragmentShader(usesTexture: boolean = true, options?: RenderOptions): string {
  const opts = options ?? { 
    renderType: 'quad', isLit: false, softParticle: true, stretchMode: 0, stretchScale: 1.0,
    blendMode: 0, orientMode: 0, flipbookColumns: 4, flipbookRows: 4,
    lineLength: 1.0, lineWidth: 0.5, velocityAlign: true
  };
  
  // NOTE: VertexOutput struct is NOT included here because render2d's createShader
  // concatenates vertex and fragment sources for WebGPU. The struct is already
  // defined in the vertex shader.
  //
  // Texture bindings are handled by the render2d bind group layout:
  // - @group(0) @binding(1) sampler
  // - @group(0) @binding(2) texture_2d<f32>
  // ONLY include these when usesTexture is true, otherwise bind group layout won't have them!

  // ── Lit (Forward+) fragment shader ──
  // Self-contained WGSL — the @include preprocessor was removed (Slang migration). The lighting/shadow structs
  // + helpers below are inlined verbatim from the former common/lighting.wgsl + common/lighting-shadow.wgsl
  // (VFX uses evaluateLight, NOT evaluateLightWithIES, so IES is omitted). LightUniforms is the prefix-only
  // layout — VFX reads only .screenSize / .ambientColor; the engine uploads a larger buffer (append-safe).
  // Reads Forward+ tile light data from group(1) bindings.
  if (opts.isLit && usesTexture) {
    return `
struct GPULight {
  positionAndRadius:  vec4f,
  colorAndIntensity:  vec4f,
  directionAndType:   vec4f,
  spotParams:         vec4f,
  iesParams:          vec4f,
  volumetricParams:   vec4f,
}

struct LightUniforms {
  ambientColor:       vec4f,
  lightCountAndFlags: vec4u,
  screenSize:         vec2f,
  invScreenSize:      vec2f,
  invViewProjection:  mat4x4f,
}

const LIGHT_TYPE_POINT: f32       = 0.0;
const LIGHT_TYPE_SPOT: f32        = 1.0;
const LIGHT_TYPE_DIRECTIONAL: f32 = 2.0;

const SHADOW_PI: f32 = 3.14159265358979;
const SHADOW_TWO_PI: f32 = 6.28318530717959;

override SHADOW_MAP_RES: u32 = 512u;
override SHADOW_PCF_SAMPLES: i32 = 33;

fn attenuatePoint(dist: f32, radius: f32, falloff: f32) -> f32 {
  if (dist >= radius) { return 0.0; }
  return pow(1.0 - dist / radius, falloff);
}

fn attenuateSpot(fragDir: vec2f, lightDir: vec2f, cosInner: f32, cosOuter: f32) -> f32 {
  let cosAngle = dot(normalize(fragDir), lightDir);
  return clamp((cosAngle - cosOuter) / (cosInner - cosOuter), 0.0, 1.0);
}

fn evaluateLight(light: GPULight, worldPos: vec2f, normal: vec2f) -> vec3f {
  let lightColor = light.colorAndIntensity.rgb;
  let intensity  = light.colorAndIntensity.a;
  let lightType  = light.directionAndType.w;

  if (abs(lightType - LIGHT_TYPE_DIRECTIONAL) < 0.5) {
    let dir = light.directionAndType.xy;
    let NdotL = max(dot(normal, -dir), 0.0);
    return lightColor * intensity * NdotL;
  }

  let lightPos = light.positionAndRadius.xy;
  let radius   = light.positionAndRadius.z;
  let falloff  = light.positionAndRadius.w;

  let toLight  = lightPos - worldPos;
  let dist     = length(toLight);
  let dir      = toLight / max(dist, 0.0001);

  let atten = attenuatePoint(dist, radius, falloff);
  if (atten <= 0.0) { return vec3f(0.0); }

  if (abs(lightType - LIGHT_TYPE_SPOT) < 0.5) {
    let lightDir   = light.directionAndType.xy;
    let cosInner   = light.directionAndType.z;
    let cosOuter   = light.spotParams.x;
    let spotAtten  = attenuateSpot(-dir, lightDir, cosInner, cosOuter);
    let NdotL      = max(dot(normal, dir), 0.0);
    return lightColor * intensity * atten * spotAtten * NdotL;
  }

  return lightColor * intensity * atten;
}

fn sampleShadow1D(baseIdx: u32, texelF: f32) -> f32 {
  let smRes = SHADOW_MAP_RES;
  let t0 = u32(floor(texelF)) % smRes;
  let t1 = (t0 + 1u) % smRes;
  let frac = fract(texelF);
  let d0 = shadowMapData[baseIdx + t0];
  let d1 = shadowMapData[baseIdx + t1];
  return mix(d0, d1, frac);
}

fn sampleShadowPCF(lightPos: vec2f, fragPos: vec2f, shadowIdx: i32, softness: f32, bias: f32) -> f32 {
  if (shadowIdx < 0) { return 1.0; }

  let diff = fragPos - lightPos;
  let fragDist = length(diff);
  let angle = atan2(diff.y, diff.x);
  let normalizedAngle = (angle + SHADOW_PI) / SHADOW_TWO_PI;
  let texelF = normalizedAngle * f32(SHADOW_MAP_RES);

  let baseIdx = u32(shadowIdx) * SHADOW_MAP_RES;

  if (softness < 0.01) {
    let occDist = sampleShadow1D(baseIdx, texelF);
    let biasedOccDist = occDist * (1.0 + bias);
    return select(0.0, 1.0, fragDist <= biasedOccDist);
  }

  let kernelRadius = softness * 10.0;
  let TAP_COUNT = f32(SHADOW_PCF_SAMPLES);
  let smResF = f32(SHADOW_MAP_RES);
  let step = (2.0 * kernelRadius) / max(TAP_COUNT - 1.0, 1.0);
  let sigma = kernelRadius * 0.5;
  let invTwoSigmaSq = 1.0 / max(2.0 * sigma * sigma, 0.001);

  var weightSum = 0.0;
  var lit = 0.0;

  for (var i = 0; i < SHADOW_PCF_SAMPLES; i++) {
    let ofs = -kernelRadius + f32(i) * step;
    let w = exp(-(ofs * ofs) * invTwoSigmaSq);
    let sampleTexelF = texelF + ofs;
    let wrappedTexelF = (sampleTexelF + smResF * 2.0) % smResF;
    let occDist = sampleShadow1D(baseIdx, wrappedTexelF);
    let biasedOccDist = occDist * (1.0 + bias);
    lit += w * select(0.0, 1.0, fragDist <= biasedOccDist);
    weightSum += w;
  }

  return lit / max(weightSum, 0.001);
}

@group(0) @binding(1) var vfxSampler: sampler;
@group(0) @binding(2) var vfxTexture: texture_2d<f32>;

// Forward+ lighting bindings (group 1)
@group(1) @binding(0) var<uniform> uLighting: LightUniforms;
@group(1) @binding(1) var<storage, read> lights: array<GPULight>;
@group(1) @binding(2) var<storage, read> tileLightData: array<u32>;
@group(1) @binding(3) var<storage, read> shadowMapData: array<f32>;

// Forward+ tile constants (pipeline-overridable, must match light-cull.wgsl)
override TILE_SIZE: u32 = 16u;
override MAX_LIGHTS_PER_TILE: u32 = 64u;

@fragment
fn fs_main(input: VertexOutput) -> @location(0) vec4<f32> {
  let texColor = textureSample(vfxTexture, vfxSampler, input.uv);
  let albedo = texColor * input.color;

  if (albedo.a < 0.001) {
    discard;
  }

  // Default 2D normal — facing "up"
  let normal = vec2f(0.0, 1.0);
  let worldPos = input.worldPos;

  // Determine which tile this fragment belongs to
  let tileX = u32(input.position.x) / TILE_SIZE;
  let tileY = u32(input.position.y) / TILE_SIZE;
  let tileCountX = u32(ceil(uLighting.screenSize.x / f32(TILE_SIZE)));
  let tileIndex = tileY * tileCountX + tileX;

  // Read per-tile light count and indices
  let tileStride = 1u + MAX_LIGHTS_PER_TILE;
  let baseOffset = tileIndex * tileStride;
  let tileLightCount = min(tileLightData[baseOffset], MAX_LIGHTS_PER_TILE);

  var lightContrib = vec3f(0.0);
  for (var i = 0u; i < tileLightCount; i++) {
    let lightIdx = tileLightData[baseOffset + 1u + i];
    let light = lights[lightIdx];
    var contrib = evaluateLight(light, worldPos, normal);

    // Apply shadow attenuation
    let shadowIdx = i32(light.spotParams.y);
    if (shadowIdx >= 0) {
      let shadowSoftness = light.spotParams.z;
      let shadowBias = light.spotParams.w;
      let shadowFactor = sampleShadowPCF(light.positionAndRadius.xy, worldPos, shadowIdx, shadowSoftness, shadowBias);
      contrib *= shadowFactor;
    }

    lightContrib += contrib;
  }

  // Ambient
  let ambient = uLighting.ambientColor.rgb * uLighting.ambientColor.a;

  // Final lit color: albedo × (lights + ambient)
  let litColor = albedo.rgb * (lightContrib + ambient);

  return vec4f(litColor, albedo.a);
}`;
  }
  
  if (usesTexture) {
    // Sprite/flipbook rendering
    if (opts.renderType === 'sprite') {
      const cols = opts.flipbookColumns;
      const rows = opts.flipbookRows;
      return `
@group(0) @binding(1) var vfxSampler: sampler;
@group(0) @binding(2) var vfxTexture: texture_2d<f32>;

@fragment
fn fs_main(input: VertexOutput) -> @location(0) vec4<f32> {
  // Flipbook UV calculation
  // TODO: frame comes from particle.texCoord attribute (not yet implemented)
  // For now, use a static frame or animate based on lifetime
  let frame = 0.0;
  let frameSize = vec2<f32>(1.0 / ${cols}.0, 1.0 / ${rows}.0);
  let frameX = frame % ${cols}.0;
  let frameY = floor(frame / ${cols}.0);
  let frameOffset = vec2<f32>(frameX, frameY) * frameSize;
  let flipbookUV = frameOffset + input.uv * frameSize;
  
  let texColor = textureSample(vfxTexture, vfxSampler, flipbookUV);
  return texColor * input.color;
}`;
    }
    
    // Standard quad with texture
    return `
@group(0) @binding(1) var vfxSampler: sampler;
@group(0) @binding(2) var vfxTexture: texture_2d<f32>;

@fragment
fn fs_main(input: VertexOutput) -> @location(0) vec4<f32> {
  // Sample texture and multiply with particle color
  let texColor = textureSample(vfxTexture, vfxSampler, input.uv);
  return texColor * input.color;
}`;
  } else {
    // No texture - check softParticle option
    if (opts.softParticle) {
      // Render soft circular particle using UV coordinates
      return `
@fragment
fn fs_main(input: VertexOutput) -> @location(0) vec4<f32> {
  // Calculate distance from center (UV 0.5, 0.5)
  let centeredUV = input.uv - vec2<f32>(0.5, 0.5);
  let dist = length(centeredUV) * 2.0;
  
  // Soft circle falloff
  let alpha = 1.0 - smoothstep(0.7, 1.0, dist);
  if (alpha < 0.01) {
    discard;
  }
  
  return input.color * vec4<f32>(1.0, 1.0, 1.0, alpha);
}`;
    } else {
      // Hard-edged quad (no soft falloff)
      return `
@fragment
fn fs_main(input: VertexOutput) -> @location(0) vec4<f32> {
  return input.color;
}`;
    }
  }
}
